import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Users, Database, Activity, Zap, TrendingUp, MapPin, Phone, Mail,
  Flame, Target, Building2, HardDrive, Rocket, Link as LinkIcon, Trophy,
} from "lucide-react";
import { GlassCard, SectionTitle, StatTile } from "./_shared";

function IntegrationBadge({ ok, label }) {
  return (
    <div className={`flex items-center gap-2 px-3 py-2 border ${ok ? "border-emerald-500 bg-emerald-500/10" : "border-[var(--border)] bg-[var(--surface)]"}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${ok ? "bg-emerald-400" : "bg-zinc-700"}`} />
      <span className="text-xs font-semibold text-[var(--text)]">{label}</span>
      <span className={`ml-auto text-[10px] font-bold tabular-nums ${ok ? "text-emerald-300" : "text-zinc-500"}`}>
        {ok ? "ACTIF" : "OFF"}
      </span>
    </div>
  );
}

function Bar({ label, value, total, highlight }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-xs">
        <span className="text-[var(--text)]/75 font-medium truncate">{label}</span>
        <span className="text-zinc-400 tabular-nums font-mono shrink-0 ml-2">
          {value} · {pct}%
        </span>
      </div>
      <div className="mt-1 h-2 bg-[var(--surface-alt)] overflow-hidden rounded-full">
        <div className={`h-full ${highlight ? "bg-emerald-500" : "bg-zinc-300"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function AdminOverview() {
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [t, a] = await Promise.all([
          api.get("/admin/tech/metrics"),
          api.get("/admin/rgpd/audit?limit=10").catch(() => ({ data: [] })),
        ]);
        setData(t.data);
        setAudit(a.data || []);
      } catch {
        /* noop */
      }
    })();
  }, []);

  if (!data) return <div className="text-zinc-400 text-sm">Chargement…</div>;

  const p = data.prospects;
  const totalBySource = data.by_source.reduce((a, b) => a + b.count, 0) || 1;
  const totalByStatus = data.by_status.reduce((a, b) => a + b.count, 0) || 1;

  return (
    <div className="space-y-5">
      {/* Perf strip */}
      <div className="flex items-center gap-2 text-[11px] font-mono text-zinc-400">
        <div className="flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500/100" />
          API {data.perf.query_ms}ms
        </div>
        <span>·</span>
        <span>{data.storage.collections} collections</span>
        <span>·</span>
        <span>{data.storage.data_mb + data.storage.indexes_mb} Mo</span>
      </div>

      {/* Hero KPIs */}
      <div className="grid grid-cols-2 gap-3">
        <StatTile label="Utilisateurs" value={data.users.total} sub={`+${data.users.new_7d} / 7j`} />
        <StatTile label="Leads" value={p.total} sub={`${p.hot_leads} chauds`} />
        <StatTile label="Taux de conv." value={`${p.conversion_rate_pct}%`} sub={`${p.converted} convertis`} />
        <StatTile label="Équipes" value={data.teams.total_teams} sub={`${data.teams.total_members} membres`} />
      </div>

      {/* Intégrations — technical status */}
      <div>
        <SectionTitle icon={Zap}>Intégrations — état technique</SectionTitle>
        <div className="grid grid-cols-2 gap-2">
          <IntegrationBadge ok={data.features.emergent_llm_configured} label="Emergent LLM" />
          <IntegrationBadge ok={data.features.stripe_configured} label="Stripe" />
          <IntegrationBadge ok={data.features.pappers_configured} label="Pappers.fr" />
          <IntegrationBadge ok={data.features.resend_configured} label="Resend" />
        </div>
        {!data.features.pappers_configured && (
          <div className="mt-2 text-[11px] text-zinc-400 bg-[var(--surface-alt)]/30 border border-[var(--border)] p-2">
            <strong>Pappers non configuré</strong> — Magic Prosperr utilise SIRENE/INPI en fallback.
            Ajoutez <code className="font-mono text-[var(--text)]">PAPPERS_API_KEY</code> dans <code className="font-mono text-[var(--text)]">backend/.env</code>
            pour activer l'enrichissement premium (CA, dirigeants, santé fin.).
          </div>
        )}
      </div>

      {/* Data quality */}
      <div>
        <SectionTitle icon={Database}>Qualité de la donnée</SectionTitle>
        <GlassCard className="p-4 space-y-3">
          <Bar label="Couverture GPS" value={p.with_gps} total={p.total} />
          <Bar label="Avec téléphone" value={p.with_phone} total={p.total} />
          <Bar label="Avec email" value={p.with_email} total={p.total} />
          <Bar label="Enrichis Pappers" value={p.enriched_pappers} total={p.total} highlight />
        </GlassCard>
      </div>

      {/* Sources */}
      <div>
        <SectionTitle icon={MapPin}>Sources de leads</SectionTitle>
        <GlassCard className="p-4 space-y-3">
          {data.by_source.slice(0, 6).map((s) => (
            <Bar key={s.source} label={s.source} value={s.count} total={totalBySource} />
          ))}
        </GlassCard>
      </div>

      {/* Status distribution */}
      <div>
        <SectionTitle icon={Target}>Répartition tunnel</SectionTitle>
        <GlassCard className="p-4 space-y-3">
          {data.by_status.map((s) => (
            <Bar key={s.status} label={s.status} value={s.count} total={totalByStatus} />
          ))}
        </GlassCard>
      </div>

      {/* Top commercials 7d */}
      {data.top_commercials_7d.length > 0 && (
        <div>
          <SectionTitle icon={Trophy}>Top commerciaux · 7 derniers jours</SectionTitle>
          <GlassCard className="p-0 divide-y divide-[var(--border-soft)]">
            {data.top_commercials_7d.map((c, i) => (
              <div key={i} className="flex items-center gap-3 p-3">
                <div className="w-7 h-7 rounded-full bg-emerald-500 text-white grid place-items-center font-display font-semibold text-xs shrink-0 border border-emerald-500">
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0 text-sm font-semibold text-[var(--text)] truncate">{c.name}</div>
                <div className="text-sm font-bold text-[var(--text)] tabular-nums font-mono">
                  {c.actions} <span className="text-zinc-400 font-medium">actions</span>
                </div>
              </div>
            ))}
          </GlassCard>
        </div>
      )}

      {/* Storage */}
      <div>
        <SectionTitle icon={HardDrive}>Stockage DB</SectionTitle>
        <GlassCard className="p-4 grid grid-cols-3 gap-3 text-center">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-400">Données</div>
            <div className="font-display font-bold text-xl text-[var(--text)] tabular-nums">{data.storage.data_mb}</div>
            <div className="text-[10px] text-zinc-400">Mo</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-400">Indexes</div>
            <div className="font-display font-bold text-xl text-[var(--text)] tabular-nums">{data.storage.indexes_mb}</div>
            <div className="text-[10px] text-zinc-400">Mo</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-400">Collections</div>
            <div className="font-display font-bold text-xl text-[var(--text)] tabular-nums">{data.storage.collections}</div>
            <div className="text-[10px] text-zinc-400">tables</div>
          </div>
        </GlassCard>
      </div>

      {/* Audit log */}
      <div>
        <SectionTitle icon={Activity}>Activité admin récente</SectionTitle>
        {audit.length === 0 ? (
          <div className="text-xs text-zinc-400 border border-[var(--border)] p-4 text-center">
            Aucun événement admin récent
          </div>
        ) : (
          <GlassCard className="p-0 divide-y divide-[var(--border-soft)]">
            {audit.slice(0, 10).map((e, i) => (
              <div key={i} className="flex items-start gap-3 p-3">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-2 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-zinc-200 font-mono">{e.event}</div>
                  <div className="text-[11px] text-zinc-400 truncate">
                    {e.actor_email}
                    {e.target_email && ` → ${e.target_email}`}
                  </div>
                </div>
                <div className="text-[10px] text-zinc-400 shrink-0 font-mono">
                  {new Date(e.at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
            ))}
          </GlassCard>
        )}
      </div>
    </div>
  );
}
